import React, {
  forwardRef,
  useState,
  useImperativeHandle,
  useRef,
} from "react";
import { Input, Modal, Form, Row, Col, Button, message } from "antd";
import { ReactComponent as CloseIcon } from "@/assets/icons/closeicon.svg";
import {
  requestSmsCode,
  requestBindAccount,
  accountUnBind,
} from "@/services/api";
import rules from "@/config/rules";

/**
 *  账号绑定弹窗
 * @returns
 */
const AccountBindingModal = forwardRef(({ handleReloadList }, ref) => {
  const [accountBindModalVisibleObj, setAccountBindModalVisibleObj] = useState(
    {}
  );
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isShowCode, setIsShowCode] = useState(false);
  const [time, setTime] = useState(60);
  const [emailCodeToken, setEmailCodeToken] = useState("");
  const [form] = Form.useForm();
  const formRef = useRef();

  useImperativeHandle(
    ref,
    () => {
      return {
        /**
         * flag: 设置弹窗开启/关闭
         * type: 绑定/修改的类型(type:1 手机号， type: 2 邮箱)
         * editType: 编辑的类型(bind:绑定， update: 修改)
         */
        setModalVisible: (flag, type, editType) => {
          formRef?.current?.resetFields();
          setAccountBindModalVisibleObj({
            open: flag,
            type: type,
            editType: editType,
          });
        },
      };
    },
    []
  );

  //获取验证码
  const sendCode = () => {
    if (isShowCode) {
      // 倒计时未结束,不能重复点击
      return;
    }
    let params = {
      brand_id: 7,
      scene: 6,
    };
    //获取手机号验证码
    if (accountBindModalVisibleObj.type === 1) {
      params = {
        ...params,
        type: 1,
        phone: form.getFieldValue("username"),
      };
    } else {
      params = {
        ...params,
        type: 2,
        email: form.getFieldValue("username"),
        lang: "zh-cn",
      };
    }
    requestSmsCode(params)
      .then((res) => {
        if (res.code === 200) {
          setIsShowCode(true);
          // 倒计时
          const active = setInterval(() => {
            setTime((preSecond) => {
              if (preSecond <= 1) {
                setIsShowCode(false);
                clearInterval(active);
                // 重置秒数
                return 60;
              }
              return preSecond - 1;
            });
          }, 1000);
          setEmailCodeToken(res?.data?.token);
        } else if (res.code === 401412) {
          message.warn("该手机号已绑定其他账号");
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  /**
   * 表单提交
   */
  const handleOk = () => {
    //判断表单验证是否通过
    form
      .validateFields()
      .then(() => {
        form.submit();
        setSubmitLoading(true);
        let data = {};
        //账号绑定
        if (accountBindModalVisibleObj.type === 1) {
          //手机号绑定
          data = {
            ...form.getFieldsValue(),
            brand_id: 7,
          };
        } else {
          //邮箱绑定
          data = {
            ...form.getFieldsValue(),
            brand_id: 7,
            token: emailCodeToken,
          };
        }
        requestBindAccount(data)
          .then((res) => {
            if (res.code === 200) {
              //绑定手机/邮箱成功后重新获取列表数据刷新
              accountBindModalVisibleObj.editType === "bind"
                ? message.success("绑定成功")
                : message.success("修改成功");
              handleReloadList();
              setAccountBindModalVisibleObj({ open: false });
            }
          })
          .catch((error) => {
            console.log(error);
          });
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  return (
    <Modal
      title={false}
      open={accountBindModalVisibleObj.open}
      closeIcon={<CloseIcon />}
      onCancel={() => setAccountBindModalVisibleObj({ open: false })}
      width={508}
      centered
      maskClosable={false}
      className="phone_binding_modal"
      footer={[
        <Button
          key="back"
          type="primary"
          className="btn btn-lg cancal"
          loading={submitLoading}
          onClick={handleOk}
        >
          确认
        </Button>,
        <Button
          key="submit"
          className="btn btn-lg submit mt-3 mt-lg-0"
          onClick={() => setAccountBindModalVisibleObj({ open: false })}
        >
          取消
        </Button>,
      ]}
    >
      <h4 className="title">
        {accountBindModalVisibleObj?.editType === "bind"
          ? accountBindModalVisibleObj?.type === 1
            ? "绑定新手机号"
            : "绑定新邮箱"
          : accountBindModalVisibleObj?.type === 1
          ? "修改手机号"
          : "修改邮箱"}
      </h4>
      <Form name="basic" form={form} ref={formRef}>
        {accountBindModalVisibleObj?.type === 1 ? (
          <Form.Item
            label=""
            name="username"
            rules={[
              { required: true, message: "请输入您的手机号!" },
              {
                pattern: new RegExp(rules.phone),
                message: "手机号格式不正确，请重新输入",
              },
            ]}
          >
            {accountBindModalVisibleObj.editType === "bind" ? (
              <Input placeholder="输入手机号" />
            ) : (
              <Input placeholder="输入新的手机号" />
            )}
          </Form.Item>
        ) : (
          <Form.Item
            label=""
            name="username"
            rules={[
              { required: true, message: "请输入您的邮箱!" },
              {
                pattern: new RegExp(rules.email),
                message: "邮箱格式错误，请重新输入",
              },
            ]}
          >
            {accountBindModalVisibleObj.editType === "bind" ? (
              <Input placeholder="输入邮箱" />
            ) : (
              <Input placeholder="输入新的邮箱" />
            )}
          </Form.Item>
        )}
        <Form.Item label="">
          <Row justify="space-between">
            <Col xs={14} md={17}>
              <Form.Item
                name="code"
                noStyle
                rules={[{ required: true, message: "请输入您的验证码!" }]}
              >
                <Input placeholder="验证码" />
              </Form.Item>
            </Col>
            <Col xs={10} md={7} style={{ textAlign: "end" }}>
              <Button
                type="primary"
                ghost
                className="sms_code"
                onClick={sendCode}
                disabled={isShowCode ? true : false}
              >
                {isShowCode ? `${time}秒后重试` : "获取验证码"}
              </Button>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </Modal>
  );
});

export default AccountBindingModal;
